<template>
  <div class="irq-panel">
    <!-- 상태 요약 -->
    <div class="irq-panel__stats">
      <ACard><AStat label="대기중" :value="formatNum(counts.request)" unit="건" /></ACard>
      <ACard><AStat label="승인됨" :value="formatNum(counts.approval)" unit="건" /></ACard>
      <ACard><AStat label="거절됨" :value="formatNum(counts.rejection)" unit="건" /></ACard>
      <ACard><AStat label="전체 요청" :value="formatNum(totalAll)" unit="건" /></ACard>
    </div>

    <!-- 상태 탭 -->
    <div class="p-tabs irq-panel__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeStatus === tab.value }]"
        @click="changeTab(tab.value)"
      >
        <i class="material-icons-round irq-panel__tab-icon">{{ tab.icon }}</i>
        {{ tab.label }}
        <span v-if="tabCount(tab.value) > 0" class="p-tabs__count">{{ tabCount(tab.value) }}</span>
      </button>
    </div>

    <!-- 요청 목록 -->
    <ACard>
      <template #title>
        <span class="irq-panel__sec-title"><i class="material-icons-round irq-panel__sec-icon">how_to_reg</i>강사 권한 요청 목록</span>
      </template>
      <template #action>
        <ABtn size="sm" icon="refresh" @click="reload">새로고침</ABtn>
      </template>

      <div v-if="loading" class="irq-panel__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>요청 목록을 불러오는 중...</p>
      </div>

      <AEmpty v-else-if="requests.length === 0" title="요청 내역이 없어요" sub="다른 상태 탭을 확인해보세요." />

      <div v-else class="irq-panel__list">
        <article
          v-for="req in requests"
          :key="req.idx"
          :class="['irq-panel__item', { 'is-pending': req.status === 'request' }]"
          @click="openDetail(req)"
        >
          <div class="irq-panel__item-body">
            <div class="irq-panel__item-meta">
              <APill :kind="statusPill(req.status)" dot>{{ statusLabel(req.status) }}</APill>
              <APill :kind="req.signup_path === 'new' ? 'yellow' : 'default'">{{ signupPathLabel(req.signup_path) }}</APill>
            </div>
            <h6 class="irq-panel__name">
              {{ req.name }}
              <small class="irq-panel__position">{{ req.org_name }} · {{ req.position }}</small>
            </h6>
            <p v-if="req.reason" class="irq-panel__reason">{{ req.reason }}</p>
            <div class="irq-panel__item-foot">
              <span><i class="material-icons-round">mail</i>{{ req.email }}</span>
              <span><i class="material-icons-round">call</i>{{ formatPhone(req.phone) }}</span>
              <span v-if="req.subject"><i class="material-icons-round">menu_book</i>{{ req.subject }}</span>
              <span><i class="material-icons-round">schedule</i>{{ formatDate(req.created_at) }}</span>
              <span v-if="req.reviewed_at"><i class="material-icons-round">verified_user</i>{{ req.reviewed_by_name || '-' }} · {{ formatDate(req.reviewed_at) }}</span>
            </div>
            <div v-if="req.status === 'rejection' && req.reject_reason" class="irq-panel__reject-reason">
              <i class="material-icons-round">block</i>{{ req.reject_reason }}
            </div>
          </div>
          <div v-if="req.status === 'request'" class="irq-panel__item-actions" @click.stop>
            <ABtn size="sm" kind="primary" icon="check" @click="approveRequest(req)">승인</ABtn>
            <ABtn size="sm" kind="danger" icon="close" @click="openRejectModal(req)">거절</ABtn>
          </div>
        </article>
      </div>

      <APagination
        v-if="totalCount > itemsPerPage"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getRequests(); }"
      />
    </ACard>

    <!-- 상세 모달 -->
    <AModal v-model="showDetailModal" size="lg">
      <template #title>
        <span class="irq-panel__modal-title"><i class="material-icons-round irq-panel__modal-icon">badge</i>강사 권한 요청 상세</span>
      </template>
      <template #sub>{{ selectedRequest?.name }}님의 요청</template>

      <div v-if="selectedRequest" class="irq-panel__detail">
        <div class="irq-panel__detail-meta">
          <APill :kind="statusPill(selectedRequest.status)" dot>{{ statusLabel(selectedRequest.status) }}</APill>
          <APill :kind="selectedRequest.signup_path === 'new' ? 'yellow' : 'default'">{{ signupPathLabel(selectedRequest.signup_path) }}</APill>
          <APill v-if="selectedRequest.user_type === 'instructor'" kind="info">강사 계정</APill>
        </div>

        <dl class="irq-panel__detail-grid">
          <div class="irq-panel__detail-row"><dt>이름</dt><dd>{{ selectedRequest.name }}</dd></div>
          <div class="irq-panel__detail-row"><dt>이메일</dt><dd class="irq-panel__mono">{{ selectedRequest.email }}</dd></div>
          <div class="irq-panel__detail-row"><dt>휴대폰</dt><dd class="irq-panel__mono">{{ formatPhone(selectedRequest.phone) }}</dd></div>
          <div class="irq-panel__detail-row"><dt>소속 기관명</dt><dd>{{ selectedRequest.org_name }}</dd></div>
          <div class="irq-panel__detail-row"><dt>직위</dt><dd>{{ selectedRequest.position }}</dd></div>
          <div class="irq-panel__detail-row"><dt>담당 과목/교재명</dt><dd>{{ selectedRequest.subject || '-' }}</dd></div>
          <div class="irq-panel__detail-row"><dt>요청일시</dt><dd>{{ formatDate(selectedRequest.created_at) }}</dd></div>
          <div v-if="selectedRequest.reviewed_at" class="irq-panel__detail-row">
            <dt>처리자 / 처리일</dt><dd>{{ selectedRequest.reviewed_by_name || '-' }} · {{ formatDate(selectedRequest.reviewed_at) }}</dd>
          </div>
        </dl>

        <div v-if="selectedRequest.reason" class="irq-panel__detail-reason">
          <div class="irq-panel__detail-label">요청 사유</div>
          <p>{{ selectedRequest.reason }}</p>
        </div>

        <div v-if="selectedRequest.status === 'rejection' && selectedRequest.reject_reason" class="irq-panel__detail-rejected">
          <div class="irq-panel__detail-label"><i class="material-icons-round">block</i>거절 사유</div>
          <p>{{ selectedRequest.reject_reason }}</p>
        </div>
      </div>

      <template #foot>
        <ABtn @click="showDetailModal = false">닫기</ABtn>
        <template v-if="selectedRequest && selectedRequest.status === 'request'">
          <ABtn kind="danger" icon="close" @click="openRejectModal(selectedRequest)">거절</ABtn>
          <ABtn kind="primary" icon="check" @click="approveRequest(selectedRequest)">승인</ABtn>
        </template>
      </template>
    </AModal>

    <!-- 거절 사유 입력 모달 -->
    <AModal v-model="showRejectModal" size="md">
      <template #title>
        <span class="irq-panel__modal-title"><i class="material-icons-round irq-panel__modal-icon irq-panel__modal-icon--err">block</i>강사 권한 요청 거절</span>
      </template>
      <template #sub>{{ rejectTarget?.name }} ({{ rejectTarget?.email }})</template>

      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">edit_note</i>
          거절 사유 <span class="p-field__req">*</span>
        </label>
        <textarea
          v-model="rejectReason"
          class="p-input p-input--area"
          rows="5"
          placeholder="예: 증빙 정보 부족 — 소속 기관 확인이 어렵습니다."
        ></textarea>
        <div class="p-field__hint"><i>info</i>입력한 사유는 거절 안내 메일에 포함되어 발송됩니다. 계정은 유지되며 재신청이 가능합니다.</div>
      </div>

      <template #foot>
        <ABtn @click="showRejectModal = false">취소</ABtn>
        <ABtn
          kind="danger-fill" icon="send"
          :disabled="!rejectReason.trim() || isProcessing"
          :loading="isProcessing"
          @click="rejectRequest"
        >거절 처리</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import AModal from "@/components/Pineapple/AModal.vue";

export default {
  name: "InstructorRequestsPanel",
  components: { ABtn, ACard, AStat, APill, AEmpty, APagination, AModal },
  emits: ["counts-changed"],
  data() {
    return {
      tabs: [
        { value: "request", label: "대기중", icon: "hourglass_empty" },
        { value: "approval", label: "승인됨", icon: "check_circle" },
        { value: "rejection", label: "거절됨", icon: "block" },
        { value: "", label: "전체", icon: "list" },
      ],
      activeStatus: "request",
      requests: [],
      totalCount: 0,
      currentPage: 1,
      itemsPerPage: 20,
      counts: { request: 0, approval: 0, rejection: 0 },
      loading: false,
      isProcessing: false,
      selectedRequest: null,
      showDetailModal: false,
      showRejectModal: false,
      rejectTarget: null,
      rejectReason: "",
    };
  },
  computed: {
    totalAll() {
      return this.counts.request + this.counts.approval + this.counts.rejection;
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    formatNum(n) { return Number(n || 0).toLocaleString(); },
    reload() {
      this.getRequests();
      this.getCounts();
    },
    changeTab(status) {
      this.activeStatus = status;
      this.currentPage = 1;
      this.getRequests();
    },
    tabCount(status) {
      if (status === "") return this.totalAll;
      return this.counts[status] || 0;
    },
    async getRequests() {
      this.loading = true;
      try {
        const params = {
          limit: this.itemsPerPage,
          offset: (this.currentPage - 1) * this.itemsPerPage,
        };
        if (this.activeStatus) params.status = this.activeStatus;
        const res = await this.$axios({
          method: "get",
          url: "/emp/instructor/requests",
          params,
          loadingBar: true,
        });
        this.requests = res.data.items || [];
        this.totalCount = res.data.total_count || 0;
      } catch (error) {
        console.error("강사 권한 요청 목록 조회 실패:", error);
        this.$swal({ icon: "error", title: "조회 실패", text: "요청 목록을 불러오는데 실패했습니다." });
      } finally {
        this.loading = false;
      }
    },
    async getCounts() {
      try {
        const statuses = ["request", "approval", "rejection"];
        const results = await Promise.all(
          statuses.map((status) =>
            this.$axios({
              method: "get",
              url: "/emp/instructor/requests",
              params: { status, limit: 1, offset: 0 },
            })
          )
        );
        statuses.forEach((status, i) => {
          this.counts[status] = results[i].data.total_count || 0;
        });
        this.$emit("counts-changed", { ...this.counts });
      } catch (error) {
        console.error("요청 건수 조회 실패:", error);
      }
    },
    statusPill(s) { return { request: "warn", approval: "ok", rejection: "err" }[s] || "default"; },
    statusLabel(s) { return { request: "대기중", approval: "승인됨", rejection: "거절됨" }[s] || s; },
    signupPathLabel(p) { return p === "new" ? "신규가입" : "기존계정"; },
    formatPhone(phone) {
      if (!phone) return "-";
      const d = String(phone).replace(/\D/g, "");
      if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
      if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
      return phone;
    },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleString("ko-KR", {
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      });
    },
    openDetail(req) {
      this.selectedRequest = req;
      this.showDetailModal = true;
    },
    async approveRequest(req) {
      const confirm = await this.$swal({
        icon: "question",
        title: "강사 권한 요청 승인",
        html: `<b>${req.name}</b> (${req.email})<br/>계정에 강사 권한이 부여되고, 승인 메일이 자동 발송됩니다.`,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: "승인",
        cancelButtonText: "취소",
      });
      if (!confirm.isConfirmed) return;

      try {
        const res = await this.$axios({
          method: "post",
          url: `/emp/instructor/requests/${req.idx}/approve`,
          loadingBar: true,
        });
        this.showDetailModal = false;
        this.$swal({ icon: "success", title: "승인 완료", text: res.data.message || "승인 처리되었습니다.", timer: 2000 });
        this.reload();
      } catch (error) {
        console.error("승인 처리 실패:", error);
        this.$swal({
          icon: "error",
          title: "승인 실패",
          text: error.response?.data?.detail || error.response?.data?.message || "승인 처리에 실패했습니다.",
          showConfirmButton: true,
        });
      }
    },
    openRejectModal(req) {
      this.rejectTarget = req;
      this.rejectReason = "";
      this.showDetailModal = false;
      this.showRejectModal = true;
    },
    async rejectRequest() {
      if (!this.rejectReason.trim()) return;
      this.isProcessing = true;
      try {
        const res = await this.$axios({
          method: "post",
          url: `/emp/instructor/requests/${this.rejectTarget.idx}/reject`,
          data: { reason: this.rejectReason.trim() },
          loadingBar: true,
        });
        this.showRejectModal = false;
        this.$swal({ icon: "success", title: "거절 완료", text: res.data.message || "거절 처리되었습니다.", timer: 2000 });
        this.reload();
      } catch (error) {
        console.error("거절 처리 실패:", error);
        this.$swal({
          icon: "error",
          title: "거절 실패",
          text: error.response?.data?.detail || error.response?.data?.message || "거절 처리에 실패했습니다.",
          showConfirmButton: true,
        });
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.irq-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__tabs { flex-wrap: wrap; }

  &__tab-icon { font-size: 15px !important; }

  &__sec-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 800; color: $p-ink;
  }

  &__sec-icon { color: $p-point-deep; font-size: 16px !important; }

  &__loading {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 48px 24px; color: $p-ink-mid;
    p { margin: 0; font-size: 12px; }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover {
      border-color: $p-point-deep;
      background: $p-panel-alt;
    }

    &.is-pending {
      border-left: 3px solid $p-point;
    }
  }

  &__item-body { flex: 1; min-width: 0; }

  &__item-meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  &__name {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__position {
    font-size: 12px;
    font-weight: 600;
    color: $p-ink-mid;
  }

  &__reason {
    margin: 0 0 8px;
    font-size: 12px;
    color: $p-ink-mid;
    line-height: 1.55;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
  }

  &__item-foot {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: $p-ink-soft;
    flex-wrap: wrap;

    > span {
      display: inline-flex;
      align-items: center;
      gap: 4px;

      > i { font-size: 13px !important; }
    }
  }

  &__reject-reason {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin-top: 8px;
    padding: 8px 10px;
    background: $p-err-soft;
    border-radius: $p-radius-sm;
    font-size: 11px;
    color: $p-err;
    line-height: 1.5;

    > i { font-size: 13px !important; flex-shrink: 0; margin-top: 1px; }
  }

  &__item-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  // ── 모달 ──
  &__modal-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 15px; font-weight: 800; color: $p-ink;
  }

  &__modal-icon {
    color: $p-point-deep; font-size: 20px !important;

    &--err { color: $p-err; }
  }

  &__detail {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__detail-meta {
    display: flex; gap: 6px; flex-wrap: wrap;
  }

  &__detail-grid {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    overflow: hidden;
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__detail-row {
    display: flex;
    border-bottom: 1px solid $p-line-soft;

    > dt {
      width: 120px;
      flex-shrink: 0;
      padding: 10px 12px;
      background: $p-panel-alt;
      font-size: 11px;
      font-weight: 800;
      color: $p-ink-mid;
    }

    > dd {
      flex: 1;
      margin: 0;
      padding: 10px 12px;
      font-size: 12px;
      color: $p-ink;
      word-break: break-all;
    }
  }

  &__mono { font-family: $p-font-mono; }

  &__detail-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 800;
    color: $p-ink-mid;
    margin-bottom: 6px;

    > i { font-size: 14px !important; }
  }

  &__detail-reason {
    > p {
      margin: 0;
      padding: 12px 14px;
      background: $p-panel-alt;
      border: 1px solid $p-line-soft;
      border-radius: $p-radius-md;
      font-size: 13px;
      color: $p-ink2;
      line-height: 1.65;
      white-space: pre-line;
    }
  }

  &__detail-rejected {
    .irq-panel__detail-label { color: $p-err; }

    > p {
      margin: 0;
      padding: 12px 14px;
      background: $p-err-soft;
      border-left: 3px solid $p-err;
      border-radius: $p-radius-md;
      font-size: 13px;
      color: $p-ink;
      line-height: 1.65;
      white-space: pre-line;
    }
  }
}
</style>
